import { createFileRoute, notFound, redirect, Link } from "@tanstack/react-router";
import { LastUpdated } from "@/components/site/LastUpdated";
import { Layout, Section, Eyebrow, CTAButton, FinalCTA, Breadcrumbs, BOOKING_URL } from "@/components/site/Layout";
import { getService, services, type Service } from "@/data/services";
import { PricingTeaser } from "@/components/site/Pricing";
import { CaseStudy, serviceCaseStudy } from "@/components/site/CaseStudy";
import { TrustSignals } from "@/components/site/TrustSignals";
import { FAQSection } from "@/components/site/FAQSection";
import { breadcrumbSchema, jsonLdScript, legalServiceSchema, organizationSchema, ogImageFor } from "@/lib/seo";

function buildServiceFaqs(s: Service) {
  const name = s.name;
  const shared = [
    { q: `How is ${name} different at PageOne vs. a generalist agency?`, a: `We only work with law firms, so every ${name.toLowerCase()} deliverable is built around legal search intent, ABA Model Rule 7 advertising compliance, and case-value economics — not generic small-business SEO tactics recycled onto a legal site.` },
    { q: `Who on your team actually does the work?`, a: `A senior legal SEO strategist owns your account and stays with it. Attorney-reviewed content is written by legal content writers; technical work is handled by senior engineers. No offshore junior teams and no bait-and-switch after signing.` },
    { q: `How do you measure success for ${name}?`, a: `We report on rankings, qualified traffic, phone calls, form submissions, consultations booked, and signed cases — not vanity metrics. Every retainer includes a monthly executive report and a quarterly strategy review.` },
    { q: `How do you report on rankings?`, a: `We report on rankings, calls, consultations and signed cases. Search positions depend on competition, relevance and Google's systems, so we do not promise a particular position or deadline. Your engagement defines the work, deliverables and reporting schedule.` },
    { q: `Do you work with our existing website or rebuild it?`, a: `Either. Many firms come to us with a site that just needs technical remediation and better content. Others need a full rebuild for Core Web Vitals, schema, and conversion. We audit first and recommend the least-invasive path that still gets results.` },
    { q: `Is your content AI-generated?`, a: `No. We use AI for research, outlining, and internal tooling. Every published word is written by a legal content specialist and reviewed by a licensed attorney for accuracy and jurisdiction. Google's Helpful Content system penalizes pure-AI legal content, and so do potential clients.` },
    { q: `Will you work with a competitor of ours?`, a: `Never in the same market and practice area. One firm per practice area per market is a policy, not a promise, and it's written into every engagement letter.` },
    { q: `What's the minimum engagement length?`, a: `12 months. SEO compounds, and shorter contracts are dishonest about how long real authority takes to build. There is no auto-renewal, no cancellation fee, and full data ownership stays with your firm.` },
    { q: `Can we speak with current clients before we sign?`, a: `Yes. After a scoping call we'll introduce you to a current client at a similar size and practice area so you can hear the unfiltered version of what working with us is like.` },
    { q: `What CRMs and call-tracking platforms do you integrate with?`, a: `Lawmatics, Clio Grow, Litify, CallRail, WhatConverts, HubSpot, Salesforce, and most other legal-industry stacks. We integrate with what you already have — we won't force a tool change.` },
  ];
  const combined = [...s.faqs, ...shared];
  const seen = new Set<string>();
  const deduped = combined.filter((f) => {
    const key = f.q.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return deduped.slice(0, Math.max(10, deduped.length));
}

export const Route = createFileRoute("/services/$slug")({
  loader: ({ params, location }) => {
    if (params.slug === "analytics-reporting") {
      throw redirect({ href: `/services/law-firm-marketing-analytics${location.searchStr}`, statusCode: 301 });
    }
    if (params.slug === "social-media-marketing") {
      throw redirect({ href: `/services/social-media-marketing-for-law-firms${location.searchStr}`, statusCode: 301 });
    }
    if (params.slug === "link-building") {
      throw redirect({ href: `/services/link-building-for-law-firms${location.searchStr}`, statusCode: 301 });
    }
    if (params.slug === "ppc-advertising") {
      throw redirect({ href: `/services/ppc-for-law-firms${location.searchStr}`, statusCode: 301 });
    }
    if (params.slug === "content-writing") {
      throw redirect({ href: `/services/legal-content-writing${location.searchStr}`, statusCode: 301 });
    }
    if (params.slug === "web-design") {
      throw redirect({ href: `/services/law-firm-website-development${location.searchStr}`, statusCode: 301 });
    }
    const aliases: Record<string, string> = {
      "ai-seo": "ai-seo-geo",
      "technical-seo": "technical-seo-for-law-firms",
      "on-page-seo": "law-firm-seo",
      "seo": "law-firm-seo",
    };
    if (aliases[params.slug]) {
      throw redirect({ to: "/services/$slug", params: { slug: aliases[params.slug] }, statusCode: 301 });
    }
    const s = getService(params.slug);
    if (!s) throw notFound();
    return { service: s };
  },
  head: ({ params, loaderData }) => {
    const s = loaderData?.service;
    if (!s) return { meta: [{ title: "Service" }] };
    const url = `/services/${params.slug}`;
    const absUrl = `https://pageonelawyers.com${url}`;
    const ogImage = ogImageFor("service", params.slug);
    return {
      meta: [
        { title: s.name },
        { name: "description", content: s.description },
        { property: "og:title", content: `${s.name} | PageOne Lawyers` },
        { property: "og:description", content: s.tagline },
        { property: "og:url", content: absUrl },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { property: "og:image:type", content: "image/svg+xml" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: `${s.name} — PageOne Lawyers` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${s.name} | PageOne Lawyers` },
        { name: "twitter:description", content: s.tagline },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: absUrl }],
      scripts: [
        jsonLdScript(organizationSchema()),
        jsonLdScript(legalServiceSchema({ name: s.name, description: s.description, url, serviceType: s.name })),
        jsonLdScript(breadcrumbSchema([
          { label: "Home", path: "/" },
          { label: "Services", path: "/services" },
          { label: s.name, path: url },
        ])),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: buildServiceFaqs(s).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          }),
        },
      ],
    };
  },
  component: ServicePage,
  notFoundComponent: () => <Layout><Section><h1>Not found</h1></Section></Layout>,
  errorComponent: () => <Layout><Section><h1>Error</h1></Section></Layout>,
});

function ServicePage() {
  const { service: s } = Route.useLoaderData() as { service: Service };
  return (
    <Layout>
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Services", href: "/services" }, { label: s.name }]} />
          <Eyebrow><span className="text-[var(--gold)]">Service</span></Eyebrow>
          <h1 className="mt-6 font-display text-5xl md:text-6xl font-semibold leading-tight max-w-3xl">{s.name}</h1><LastUpdated />
          <p className="mt-6 text-xl text-primary-foreground/85 max-w-3xl">{s.tagline}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <CTAButton to="/law-firm-seo-audit" variant="gold">Get a free SEO audit</CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">Talk to a strategist</CTAButton>
          </div>
        </div>
      </section>
      <Section>
        <p className="text-lg text-foreground leading-relaxed max-w-3xl">{s.description}</p>
      </Section>
      <div className="bg-secondary/40 border-y border-border">
        <Section>
          <Eyebrow>What you get</Eyebrow>
          <h2 className="mt-4 text-4xl font-semibold max-w-3xl">Every deliverable, documented.</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {s.whatYouGet.map((d) => (
              <div key={d} className="flex gap-4 rounded-lg bg-background border border-border p-5">
                <span className="text-[var(--gold-deep)] font-bold flex-shrink-0">✓</span>
                <p className="text-foreground">{d}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>
      <PricingTeaser title={`${s.name} plans starting at $200/month.`} sub="Every SEO package includes this service scaled to the plan tier, from local solo campaigns to full enterprise programs. Pick the plan that matches your firm's size and market." />
      <Section>
        <Eyebrow>Outcomes</Eyebrow>
        <h2 className="mt-4 text-4xl font-semibold max-w-3xl">What success looks like.</h2>
        <ul className="mt-8 space-y-4 max-w-3xl">
          {s.outcomes.map((o) => (
            <li key={o} className="flex gap-4 text-lg"><span className="text-[var(--gold-deep)] font-bold">→</span>{o}</li>
          ))}
        </ul>
      </Section>
      <CaseStudy {...serviceCaseStudy(s.name)} />
      <TrustSignals
        intro={`Every ${s.name.toLowerCase()} engagement is delivered by the same senior team, on the same guarantees, with reporting tied to signed cases — not clicks.`}
      />
      <FAQSection
        tone="muted"
        title={`${s.name}, questions firms ask.`}
        items={buildServiceFaqs(s)}
      />
      <Section>
        <h3 className="text-2xl font-semibold mb-6">Other services</h3>
        <div className="flex flex-wrap gap-2">
          {services.filter((x) => x.slug !== s.slug).map((x) => (
            <Link key={x.slug} to="/services/$slug" params={{ slug: x.slug }} className="rounded-full border border-border px-4 py-2 text-sm hover:border-[var(--gold)] hover:text-[var(--gold-deep)] transition">{x.name}</Link>
          ))}
        </div>
      </Section>
      <FinalCTA />
    </Layout>
  );
}